import { useState } from "react";
import axios from "axios";
import { toast } from 'react-toastify';
import { handleBillingError, readBlobError, downloadBlob, filenameFrom } from "./useBilling";

const useDesigns = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [bgImage, setBgImage] = useState(null);
  const [draft, setDraft] = useState(false);

  //update the template
  const update = ({ id = null, data }) => {

    if (!id) {
      return;
    }
    setLoading(true);
    axios.patch(`/users/certificates/${id}`,
      { data },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    ).
      then((response) => {
        if (response.status === 200) {
          setDraft(response.data.draft);
        }
      }).
      catch((error) => {
        console.error('Failed to save:', error);
        toast.error("Failed to save changes. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
        });
      }).
      finally(() => {
        setLoading(false);
      });
  }

  //update the template 
  const updateName = ({ id = null, name }) => {

    if (!id) {
      return;
    }
    setLoading(true);
    axios.patch(`/users/certificates/${id}/update_name`,
      { name },
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    ).
      then((response) => {
        if (response.status === 200) {
          //Show success toast 
          toast.success("Name updated successfully", {
            position: "bottom-center",
            autoClose: 1000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: false,
            progress: null,

          });
        }
      }).
      catch((error) => {
        console.log(error);
      }).
      finally(() => {
        setLoading(false);
      });
  }

  //fetch the templates
  const fetchTemplates = () => {
    axios.get("/users/templates", {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      }
    })
      .then((response) => {
        setTemplates(response.data.templates);
      }
      );

  };

  //Create a new certificate
  const create = (name) => {
    setLoading(true);
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    axios.post("/users/certificates",
      name,
      {
        headers: {
          "X-CSRF-Token": csrfToken,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        withCredentials: true,
      }
    ).
      then((response) => {
        if (response.status === 200) {
          console.log("Login successful", response.data);
          // Redirect to the dashboard
          window.location.href = response.data.redirect_url;
        }
      }).
      catch((error) => {
        console.log(error);
      }).
      finally(() => {
        setLoading(false);
      });
  }

  //Publish the design 

  const publish = ({ id }) => {
    setLoading(true)
    const csrfToken = document.querySelector('meta[name="csrf-token"]').getAttribute('content');

    axios.post(`/designs/${id}/publish`, {}, {
      headers: {
        "X-CSRF-Token": csrfToken,
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      withCredentials: true,
    }).
      then((response) => {
        if (response.status === 200) {
          toast.success("Certificate published successfully!", {
            position: "bottom-center",
            autoClose: 2000,
          });
        }
      }).catch((error) => {
        console.error('Failed to publish:', error);
        toast.error("Failed to publish certificate. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
        });
      }).finally(() => {
        setLoading(false)
      })
  }

  //Upload background image
  const uploadBg = (formData) => {
    setLoading(true);
    axios.post(`/designs/upload_bg`,
      formData,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data"
        }
      }
    ).
      then((response) => {
        if (response.status === 200) {
          setBgImage(response.data.url);
          toast.success("Background image uploaded", {
            position: "bottom-center",
            autoClose: 2000,
          });
        }
      }).
      catch((error) => {
        console.error('Failed to upload background:', error);
        toast.error("Failed to upload background image. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
        });
      }).
      finally(() => {
        setLoading(false);
      });
  }

  //Upload image
  const uploadImage = ({ formData, addImage = null, addSvgImage = null }) => {
    setLoading(true);
    axios.post(`/designs/upload_image`,
      formData,
      {
        headers: {
          Accept: "application/json",
          "Content-Type": "multipart/form-data"
        }
      }
    ).
      then((response) => {
        if (response.status === 200) {
          if (response.data.file_type === "svg") {
            addSvgImage({
              image: response.data.url,
              name: response.data.name
            });
          } else {
            addImage({
              url: response.data.url,
              name: response.data.name
            });
          }
          toast.success("Image uploaded successfully", {
            position: "bottom-center",
            autoClose: 2000,
          });
        }
      }).
      catch((error) => {
        console.error('Failed to upload image:', error);
        toast.error("Failed to upload image. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
        });
      }).
      finally(() => {
        setLoading(false);
      });
  }
  // Downloaded via XHR rather than window.open: the endpoint is metered, and a 402 JSON
  // body opened as a page would just show the user raw JSON.
  const openPdf = ({ id }) => {
    setLoading(true);

    axios.get(`/designs/${id}/pdf`, {
      responseType: "blob",
      withCredentials: true,
    }).
      then((response) => {
        downloadBlob(response.data, filenameFrom(response, `certificate-${id}.pdf`));
      }).
      catch(async (error) => {
        if (handleBillingError(await readBlobError(error))) return;

        console.error('Failed to download PDF:', error);
        toast.error("Failed to download the PDF. Please try again.", {
          position: "bottom-center",
          autoClose: 3000,
        });
      }).
      finally(() => {
        setLoading(false);
      });
  }

  const openImage = ({ id }) => {
    window.open(`/designs/${id}/png`, "self");
  }

  return {
    templates,
    fetchTemplates,
    create,
    loading,
    update,
    publish,
    uploadBg,
    bgImage,
    openImage,
    openPdf,
    updateName,
    uploadImage,
    draft
  };
}

export default useDesigns;  